import React, { useState, useRef } from "react";

export interface SkillFile {
  file: File;
  relativePath: string;
}

export const MAX_FILES = 25;
export const MAX_FILE_SIZE = 512 * 1024;

interface FileDropZoneProps {
  files: SkillFile[];
  onFilesAdded: (files: SkillFile[]) => void;
  onFileRemoved: (index: number) => void;
}

export function stripWrapperFolder(paths: string[]): string[] {
  if (paths.length === 0) return paths;
  const parts = paths.map((p) => p.split("/"));
  if (parts.some((p) => p.length < 2)) return paths;
  const prefix = parts[0][0];
  if (parts.every((p) => p[0] === prefix)) {
    const stripped = parts.map((p) => p.slice(1).join("/"));
    if (stripped.some((p) => !p)) return paths;
    return stripped;
  }
  return paths;
}

async function readDirectoryEntry(
  entry: FileSystemDirectoryEntry,
): Promise<SkillFile[]> {
  const results: SkillFile[] = [];

  async function walk(dirEntry: FileSystemDirectoryEntry): Promise<void> {
    const reader = dirEntry.createReader();
    let batch: FileSystemEntry[];
    do {
      batch = await new Promise<FileSystemEntry[]>((resolve, reject) =>
        reader.readEntries(resolve, reject),
      );
      for (const child of batch) {
        if (child.isFile) {
          const file = await new Promise<File>((resolve, reject) =>
            (child as FileSystemFileEntry).file(resolve, reject),
          );
          results.push({ file, relativePath: child.fullPath.replace(/^\//, "") });
        } else if (child.isDirectory) {
          await walk(child as FileSystemDirectoryEntry);
        }
      }
    } while (batch.length > 0);
  }

  await walk(entry);
  return results;
}

export default function FileDropZone({
  files,
  onFilesAdded,
  onFileRemoved,
}: FileDropZoneProps): JSX.Element {
  const [isDragOver, setIsDragOver] = useState(false);
  const [warnings, setWarnings] = useState<string[]>([]);
  const folderInputRef = useRef<HTMLInputElement>(null);

  function validateAndFilter(incoming: SkillFile[]): SkillFile[] {
    const msgs: string[] = [];
    const archives = incoming.filter((f) =>
      /\.(zip|tar|gz|tgz|rar|7z)$/i.test(f.relativePath),
    );
    if (archives.length > 0) {
      const names = archives.map((f) => f.relativePath).join(", ");
      msgs.push(`Archive files are not supported (unzip first): ${names}`);
    }
    const nonArchive = incoming.filter(
      (f) => !/\.(zip|tar|gz|tgz|rar|7z)$/i.test(f.relativePath),
    );
    const oversized = nonArchive.filter((f) => f.file.size > MAX_FILE_SIZE);
    if (oversized.length > 0) {
      const names = oversized.map((f) => f.relativePath).join(", ");
      msgs.push(`Skipped ${oversized.length} file(s) over 512 KB: ${names}`);
    }
    const valid = nonArchive.filter((f) => f.file.size <= MAX_FILE_SIZE);
    const totalAfterAdd = files.length + valid.length;
    if (totalAfterAdd > MAX_FILES) {
      const allowed = MAX_FILES - files.length;
      msgs.push(`Only ${MAX_FILES} files allowed. ${valid.length - allowed} file(s) were not added.`);
      setWarnings(msgs);
      return valid.slice(0, Math.max(0, allowed));
    }
    setWarnings(msgs);
    return valid;
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const entries: FileSystemEntry[] = [];
    for (const item of Array.from(e.dataTransfer.items)) {
      const entry = item.webkitGetAsEntry?.();
      if (entry) entries.push(entry);
    }

    const allFiles: SkillFile[] = [];
    let hasDirectory = false;

    for (const entry of entries) {
      if (entry.isDirectory) {
        hasDirectory = true;
        const dirFiles = await readDirectoryEntry(entry as FileSystemDirectoryEntry);
        allFiles.push(...dirFiles);
      } else if (entry.isFile) {
        const file = await new Promise<File>((resolve, reject) =>
          (entry as FileSystemFileEntry).file(resolve, reject),
        );
        allFiles.push({ file, relativePath: file.name });
      }
    }

    if (hasDirectory && allFiles.length > 0) {
      const rawPaths = allFiles.map((f) => f.relativePath);
      const stripped = stripWrapperFolder(rawPaths);
      for (let i = 0; i < allFiles.length; i++) {
        allFiles[i] = { ...allFiles[i], relativePath: stripped[i] };
      }
    }

    if (allFiles.length > 0) {
      const filtered = validateAndFilter(allFiles);
      if (filtered.length > 0) onFilesAdded(filtered);
    }
  };

  const handleFolderInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files ?? []);
    if (selected.length === 0) return;

    const skillFiles: SkillFile[] = selected.map((f) => ({
      file: f,
      relativePath: f.webkitRelativePath || f.name,
    }));

    const rawPaths = skillFiles.map((f) => f.relativePath);
    const stripped = stripWrapperFolder(rawPaths);
    const result = skillFiles.map((sf, i) => ({ ...sf, relativePath: stripped[i] }));

    const filtered = validateAndFilter(result);
    if (filtered.length > 0) onFilesAdded(filtered);
    if (folderInputRef.current) folderInputRef.current.value = "";
  };

  const hasNestedFiles = files.some((f) => f.relativePath.includes("/"));

  return (
    <div>
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => folderInputRef.current?.click()}
        style={{
          border: `2px dashed ${isDragOver ? "#0078d4" : "#ccc"}`,
          borderRadius: "8px",
          padding: "2rem",
          textAlign: "center",
          cursor: "pointer",
          backgroundColor: isDragOver ? "#e6f0ff" : "#fafafa",
          transition: "border-color 0.2s, background-color 0.2s",
        }}
      >
        <p style={{ margin: 0, color: "#666", fontSize: "0.95rem" }}>
          {isDragOver
            ? "Drop files or folders here"
            : "Drag and drop files here, or click to browse"}
        </p>
        <p style={{ margin: "0.5rem 0 0", color: "#999", fontSize: "0.8rem" }}>
          SKILL.md, README.md, .mcp.json, and other skill files
        </p>
        <p style={{ margin: "0.25rem 0 0", color: "#bbb", fontSize: "0.75rem" }}>
          Up to {MAX_FILES} files, {MAX_FILE_SIZE / 1024} KB each
        </p>
        <input
          ref={folderInputRef}
          type="file"
          // @ts-expect-error webkitdirectory is not in React's types
          webkitdirectory=""
          onChange={handleFolderInput}
          style={{ display: "none" }}
        />
      </div>

      {warnings.length > 0 && (
        <div style={{
          backgroundColor: "#fff8e1",
          border: "1px solid #f0ad4e",
          borderRadius: "4px",
          padding: "0.5rem 0.75rem",
          marginTop: "0.5rem",
          fontSize: "0.85rem",
          color: "#8a6d3b",
        }}>
          {warnings.map((w) => (
            <p key={w} style={{ margin: 0 }}>{w}</p>
          ))}
        </div>
      )}

      {files.length > 0 && (
        <ul style={{ listStyle: "none", padding: 0, margin: "0.75rem 0 0" }}>
          {hasNestedFiles
            ? <FileTree files={files} onFileRemoved={onFileRemoved} />
            : files.map((sf, i) => (
                <FileRow
                  key={`${sf.relativePath}-${sf.file.size}`}
                  name={sf.file.name}
                  size={sf.file.size}
                  onRemove={() => onFileRemoved(i)}
                />
              ))}
        </ul>
      )}
    </div>
  );
}

function FileRow({
  name,
  size,
  indent,
  onRemove,
}: {
  name: string;
  size: number;
  indent?: number;
  onRemove: () => void;
}): JSX.Element {
  return (
    <li
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "0.5rem 0.75rem",
        paddingLeft: indent ? `${0.75 + indent * 1.25}rem` : "0.75rem",
        backgroundColor: "#f5f5f5",
        borderRadius: "4px",
        marginBottom: "0.25rem",
        fontSize: "0.9rem",
      }}
    >
      <span style={{ color: "#333" }}>
        {name}{" "}
        <span style={{ color: "#999" }}>
          ({(size / 1024).toFixed(1)} KB)
        </span>
      </span>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        style={{
          background: "none",
          border: "none",
          color: "#cc0000",
          cursor: "pointer",
          fontSize: "0.85rem",
          padding: "0.25rem 0.5rem",
        }}
      >
        Remove
      </button>
    </li>
  );
}

interface TreeNode {
  name: string;
  fileIndex?: number;
  fileSize?: number;
  children: TreeNode[];
}

function buildTree(files: SkillFile[]): TreeNode {
  const root: TreeNode = { name: "", children: [] };

  for (let i = 0; i < files.length; i++) {
    const parts = files[i].relativePath.split("/");
    let current = root;
    for (let j = 0; j < parts.length; j++) {
      const part = parts[j];
      const isFile = j === parts.length - 1;
      let child = current.children.find((c) => c.name === part);
      if (!child) {
        child = { name: part, children: [] };
        if (isFile) {
          child.fileIndex = i;
          child.fileSize = files[i].file.size;
        }
        current.children.push(child);
      }
      current = child;
    }
  }

  return root;
}

function FileTree({
  files,
  onFileRemoved,
}: {
  files: SkillFile[];
  onFileRemoved: (index: number) => void;
}): JSX.Element {
  const tree = buildTree(files);

  function renderNode(node: TreeNode, depth: number): JSX.Element[] {
    const elements: JSX.Element[] = [];
    const sorted = [...node.children].sort((a, b) => {
      const aIsDir = a.children.length > 0 && a.fileIndex === undefined;
      const bIsDir = b.children.length > 0 && b.fileIndex === undefined;
      if (aIsDir !== bIsDir) return aIsDir ? -1 : 1;
      return a.name.localeCompare(b.name);
    });

    for (const child of sorted) {
      if (child.fileIndex !== undefined) {
        elements.push(
          <FileRow
            key={`file-${child.fileIndex}`}
            name={child.name}
            size={child.fileSize!}
            indent={depth}
            onRemove={() => onFileRemoved(child.fileIndex!)}
          />,
        );
      } else {
        elements.push(
          <li
            key={`dir-${depth}-${child.name}`}
            style={{
              padding: "0.35rem 0.75rem",
              paddingLeft: `${0.75 + depth * 1.25}rem`,
              color: "#666",
              fontSize: "0.85rem",
              fontWeight: 600,
            }}
          >
            {child.name}/
          </li>,
        );
        elements.push(...renderNode(child, depth + 1));
      }
    }

    return elements;
  }

  return <>{renderNode(tree, 0)}</>;
}
